import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPatch, apiPostForm, AuthError } from '../api';
import { useClient } from '../ClientContext';
import Icon from '../components/Icon';

/**
 * Creative Library.
 *
 * A library of pictures that showed no pictures. Every asset was a row of
 * text and the only way to see one was to open a modal, one at a time — which
 * is not how anyone checks whether the right file is in the library, or
 * whether the thing they are about to publish to Meta is the thing they meant.
 *
 * The reason it was built that way is real and is respected here: each preview
 * costs a signed-URL round trip, the endpoint is rate limited at 40 a minute,
 * and minting one per row on load would be two hundred requests for a table
 * nobody has clicked. So thumbnails are minted only as cards scroll into view,
 * three at a time, and cached until the link expires.
 *
 * Three other things changed:
 *
 *   Approving is a confirm, not a click. It publishes the file to Meta and
 *   cannot be undone from this screen, and the old control was a small button
 *   in a table row next to a name, with no picture anywhere near it.
 *
 *   The file is shown while it is being named. Naming a creative you cannot
 *   see is how "Hero_16x9" ends up on the wrong image.
 *
 *   "Approved, not published" no longer looks like "Pending". It is the state
 *   that silently blocks every campaign naming the asset, and it used to
 *   announce itself only by an agent being refused.
 */

const CAMPAIGN_TYPES = [
  { value: 'OUTCOME_LEADS', label: 'Leads' },
  { value: 'OUTCOME_TRAFFIC', label: 'Traffic' },
  { value: 'OUTCOME_AWARENESS', label: 'Awareness' },
  { value: 'OUTCOME_SALES', label: 'Sales' },
];

const TYPE_LABEL = CAMPAIGN_TYPES.reduce((a, c) => ({ ...a, [c.value]: c.label }), {});

const FILTERS = [
  { value: 'ALL',     label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'NEEDS',   label: 'Not published' },
  { value: 'LIVE',    label: 'Live on Meta' },
];

const VIEW_KEY = 'bidsense.creative.view';

/* ─────────────────────────────────────────────────────────────────────────
   Signed URLs: lazy, capped, cached

   /creative-library/{account}/asset/{id}/url is rate limited at 40 a minute.
   A grid that fired one request per card would spend the whole budget on
   thumbnails nobody scrolled to, so requests are queued three at a time and
   the answer is kept until the link is close to expiring.
   ───────────────────────────────────────────────────────────────────────── */

const MAX_INFLIGHT = 3;
let inflight = 0;
const queue = [];
const urlCache = new Map();

function pump() {
  while (inflight < MAX_INFLIGHT && queue.length) {
    const job = queue.shift();
    inflight += 1;
    job().finally(() => { inflight -= 1; pump(); });
  }
}

function schedule(job) {
  queue.push(job);
  pump();
}

function cachedUrl(key) {
  const hit = urlCache.get(key);
  if (hit && hit.expiresAt > Date.now()) return hit.url;
  if (hit) urlCache.delete(key);
  return null;
}

function useAssetUrl(accountId, assetId, active) {
  const key = `${accountId}:${assetId}`;
  const [state, setState] = useState(() => {
    const hit = cachedUrl(key);
    return hit ? { status: 'ready', url: hit } : { status: 'idle', url: null };
  });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!active || !accountId || !assetId) return undefined;

    const hit = cachedUrl(key);
    if (hit) { setState({ status: 'ready', url: hit }); return undefined; }

    let cancelled = false;
    setState({ status: 'loading', url: null });

    schedule(async () => {
      if (cancelled) return;
      try {
        const data = await apiGet(`/creative-library/${accountId}/asset/${assetId}/url`);
        // A minute of headroom, so a link is never handed to an <img> in the
        // last seconds of its life.
        const minutes = Math.max(1, (data.expires_in_minutes || 10) - 1);
        urlCache.set(key, { url: data.url, expiresAt: Date.now() + minutes * 60000 });
        if (!cancelled) setState({ status: 'ready', url: data.url, expiresInMinutes: data.expires_in_minutes });
      } catch (err) {
        const message = err instanceof AuthError
          ? err.message
          : err.message || 'Could not load this creative.';
        if (!cancelled) setState({ status: 'error', url: null, message });
      }
    });

    return () => { cancelled = true; };
  }, [key, active, accountId, assetId, nonce]);

  // The link can be valid while the object behind it is not — a file removed
  // from the bucket under the record. Drop the cached URL so a retry re-mints
  // rather than handing back the same dead link.
  const failed = useCallback((message) => {
    urlCache.delete(key);
    setState({ status: 'error', url: null, message });
  }, [key]);

  const retry = useCallback(() => {
    urlCache.delete(key);
    setNonce((n) => n + 1);
  }, [key]);

  return { ...state, retry, failed };
}

function useInView(ref) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { rootMargin: '250px' });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, seen]);
  return seen;
}

/* ─────────────────────────────────────────────────────────────────────────
   Small helpers
   ───────────────────────────────────────────────────────────────────────── */

function ago(value) {
  if (!value) return null;
  const then = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(then.getTime())) return null;
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} days ago`;
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const statusOf = (a) => (a.is_approved ? (a.uploaded_to_meta ? 'live' : 'needs') : 'pending');

function StatusPill({ asset }) {
  const status = statusOf(asset);
  if (status === 'live')  return <span className="pill approved">Live on Meta</span>;
  if (status === 'needs') return <span className="pill flagged">Not published</span>;
  return <span className="pill observe">Pending</span>;
}

const fileSize = (bytes) => {
  if (!bytes) return null;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/* ─────────────────────────────────────────────────────────────────────────
   Thumbnail
   ───────────────────────────────────────────────────────────────────────── */

function Thumbnail({ accountId, asset, onOpen, className = 'cl-thumb', compact = false }) {
  const holder = useRef(null);
  const inView = useInView(holder);
  const { status, url, failed } = useAssetUrl(accountId, asset.asset_id, inView);

  return (
    <button
      ref={holder}
      type="button"
      className={`${className} cl-checker`}
      onClick={() => onOpen(asset)}
      title={`View ${asset.asset_name}`}
      aria-label={`View ${asset.asset_name}`}
    >
      {status === 'loading' && <span className="cl-shim skeleton" />}
      {status === 'ready' && url && (
        <img
          src={url}
          alt={asset.asset_name}
          loading="lazy"
          onError={() => failed('The file could not be loaded from storage.')}
        />
      )}
      {(status === 'error' || (status === 'idle' && inView)) && (
        <span className="cl-ph">
          <Icon name="image" size={compact ? 16 : 20} strokeWidth={1.8} />
          {!compact && (asset.format || 'No preview')}
        </span>
      )}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Card
   ───────────────────────────────────────────────────────────────────────── */

function AssetCard({ accountId, asset, onOpen, onApprove }) {
  const status = statusOf(asset);
  const when = ago(asset.created_at);

  return (
    <div className={`cl-card${status === 'needs' ? ' needs' : ''}`}>
      <Thumbnail accountId={accountId} asset={asset} onOpen={onOpen} />
      <div className="cl-body">
        <div className="cl-title" title={asset.asset_name}>{asset.asset_name}</div>
        <div className="cl-sub">
          {asset.format || 'Size unknown'}
          {asset.campaign_type ? ` · ${TYPE_LABEL[asset.campaign_type] || asset.campaign_type}` : ''}
        </div>
        {when && <div className="cl-sub">Added {when}</div>}
      </div>

      {status === 'needs' && (
        <div className="cl-needs">
          <Icon name="alert" size={13} strokeWidth={2.2} />
          <span>Approved but not on Meta. Campaigns naming this asset will be refused.</span>
        </div>
      )}

      <div className="cl-foot">
        <StatusPill asset={asset} />
        {!asset.is_approved && (
          <button className="btn btn-approve" onClick={() => onApprove(asset)}>
            Approve
          </button>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Preview dialog
   ───────────────────────────────────────────────────────────────────────── */

function PreviewDialog({ accountId, asset, onClose, onApprove, onEdit }) {
  const { status, url, message, expiresInMinutes, retry, failed } = useAssetUrl(accountId, asset.asset_id, true);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const added = ago(asset.created_at);
  const published = ago(asset.uploaded_to_meta_at);

  return (
    <div
      className="scrim"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Creative preview: ${asset.asset_name}`}
    >
      <div className="cl-pv" onClick={(e) => e.stopPropagation()}>
        <div className="cl-pv-head">
          <div style={{ minWidth: 0 }}>
            <div className="cl-pv-name">{asset.asset_name}</div>
            <div className="cl-pv-meta">
              {asset.asset_type}
              {asset.format ? ` · ${asset.format}` : ''}
              {asset.campaign_type ? ` · ${TYPE_LABEL[asset.campaign_type] || asset.campaign_type}` : ''}
              {added ? ` · added ${added}` : ''}
            </div>
          </div>
          <button className="cl-pv-close" onClick={onClose} aria-label="Close preview">
            <Icon name="x" size={20} strokeWidth={2.2} />
          </button>
        </div>

        <div className="cl-pv-stage cl-checker">
          {status === 'loading' && <div className="cl-pv-msg">Loading creative…</div>}

          {status === 'error' && (
            <div className="cl-pv-msg bad">
              {message}
              <div>
                <button className="btn btn-secondary" onClick={retry}>Try again</button>
              </div>
            </div>
          )}

          {status === 'ready' && url && (
            <img
              src={url}
              alt={asset.asset_name}
              onError={() => failed('The link was issued but the file could not be loaded. It may have been removed from storage.')}
            />
          )}
        </div>

        <div className="cl-pv-foot">
          <div className="cl-pv-facts">
            <StatusPill asset={asset} />
            {asset.approved_by && <span>Approved by {asset.approved_by}</span>}
            {published && <span>Published to Meta {published}</span>}
            {status === 'ready' && expiresInMinutes && (
              <span>Link expires in {expiresInMinutes} minutes</span>
            )}
          </div>

          <div className="cl-pv-btns">
            {/* Editing opens a copy. Saving from the studio creates a new asset
                rather than overwriting this one, so a creative already live on
                Meta cannot be altered underneath the campaigns using it. */}
            {asset.asset_type === 'IMAGE' && (
              <button className="btn btn-secondary" onClick={() => onEdit(asset)}>
                Edit in Studio
              </button>
            )}
            {status === 'ready' && url && (
              <a className="btn btn-secondary" href={url} target="_blank" rel="noopener noreferrer">
                Open full size
              </a>
            )}
            {!asset.is_approved && (
              <button className="btn btn-approve" onClick={() => onApprove(asset)}>
                Approve
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Approve confirm

   Approval publishes the file to Meta and cannot be undone from this screen.
   That deserves more than a small button beside a filename.
   ───────────────────────────────────────────────────────────────────────── */

function ApproveDialog({ accountId, asset, busy, onCancel, onConfirm }) {
  const { status, url } = useAssetUrl(accountId, asset.asset_id, true);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  return (
    <div className="scrim" role="dialog" aria-modal="true" aria-label="Approve this creative">
      <div className="dialog">
        <h3>Approve and publish?</h3>
        <div className="body">
          <div className="cl-confirm-top">
            <div className="cl-confirm-shot cl-checker">
              {status === 'ready' && url
                ? <img src={url} alt={asset.asset_name} />
                : <Icon name="image" size={22} strokeWidth={1.8} />}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="cl-confirm-name">{asset.asset_name}</div>
              <div className="cl-confirm-sub">
                {asset.asset_type}
                {asset.format ? ` · ${asset.format}` : ''}
                {asset.campaign_type ? ` · ${TYPE_LABEL[asset.campaign_type] || asset.campaign_type}` : ''}
              </div>
            </div>
          </div>

          <ul>
            <li>
              <Icon name="right" size={15} strokeWidth={2.2} />
              The file is uploaded to Meta and becomes usable in live campaigns.
            </li>
            <li>
              <Icon name="spark" size={15} strokeWidth={2.2} />
              Agents can then reference it by name when building.
            </li>
            <li>
              <Icon name="alert" size={15} strokeWidth={2.2} />
              Your name is recorded against the approval, and it cannot be undone here.
            </li>
          </ul>
        </div>
        <div className="foot">
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="btn btn-approve" onClick={() => onConfirm(asset)} disabled={busy}>
            {busy ? 'Publishing…' : 'Approve and publish'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Page
   ───────────────────────────────────────────────────────────────────────── */

export default function CreativeLibrary() {
  // Creative is uploaded to Meta, so always the Meta account.
  const { metaAccountId: accountId, selected, isAll } = useClient();
  const navigate = useNavigate();

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'; } catch { return 'grid'; }
  });

  const [pendingFile, setPendingFile] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [measuring, setMeasuring] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [form, setForm] = useState({ campaignType: 'OUTCOME_LEADS', format: '', assetName: '' });

  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const fileInputRef = useRef(null);

  const loadAssets = useCallback(() => {
    if (!accountId) { setLoading(false); return; }
    apiGet(`/creative-library/${accountId}`)
      .then((data) => { setAssets(data.assets || []); setLoading(false); })
      .catch((err) => {
        setError(err instanceof AuthError ? err.message : err.message || 'Could not load the creative library.');
        setLoading(false);
      });
  }, [accountId]);

  // A part-completed upload belongs to the client that was on screen when it
  // started, so switching client clears it rather than filing it elsewhere.
  useEffect(() => {
    setPendingFile(null);
    setLocalPreview((current) => { if (current) URL.revokeObjectURL(current); return null; });
    setMeasuring(false);
    setPreview(null);
    setConfirming(null);
    setSearch('');
    setForm({ campaignType: 'OUTCOME_LEADS', format: '', assetName: '' });
    setError(null);
    setLoading(true);
    loadAssets();
  }, [accountId, loadAssets]);

  useEffect(() => {
    try { localStorage.setItem(VIEW_KEY, view); } catch { /* private mode */ }
  }, [view]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3600);
    return () => clearTimeout(t);
  }, [toast]);

  // The object URL for the file being named is released when it is replaced or
  // the page goes away, rather than being left to the tab's lifetime.
  useEffect(() => () => { if (localPreview) URL.revokeObjectURL(localPreview); }, [localPreview]);

  /* ── Upload ─────────────────────────────────────────────────────────── */

  const suggestName = (format, campaignType) => {
    const campaignLabel = TYPE_LABEL[campaignType] || campaignType;
    const clientLabel = (selected?.name || 'Client').replace(/\s+/g, '');
    return [clientLabel, campaignLabel, format].filter(Boolean).join('_');
  };

  // Matches the database index, which is on lower(trim(asset_name)) — so the
  // warning appears for exactly the names the server would reject.
  const isDuplicate = (name) => {
    const normalised = (name || '').trim().toLowerCase();
    if (!normalised) return false;
    return assets.some((a) => (a.asset_name || '').trim().toLowerCase() === normalised);
  };

  const selectFile = (file) => {
    setPendingFile(file);
    setError(null);

    setLocalPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return file.type.startsWith('image') ? URL.createObjectURL(file) : null;
    });

    if (file.type.startsWith('image')) {
      // Decoding is asynchronous and the Upload button used to be live while it
      // ran, so a quick click sent the form before the dimensions had landed.
      setMeasuring(true);
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const format = `${img.width}x${img.height}`;
        setForm((f) => ({
          ...f,
          format,
          assetName: f.assetName || suggestName(format, f.campaignType),
        }));
        setMeasuring(false);
        URL.revokeObjectURL(objectUrl);
      };
      // A file the browser cannot decode is not fatal — the server reads the
      // bytes itself and rejects it with a better message than this could.
      img.onerror = () => { setMeasuring(false); URL.revokeObjectURL(objectUrl); };
      img.src = objectUrl;
    } else {
      setForm((f) => ({ ...f, assetName: f.assetName || file.name.replace(/\.[^.]+$/, '') }));
    }
  };

  const cancelUpload = () => {
    setPendingFile(null);
    setLocalPreview((current) => { if (current) URL.revokeObjectURL(current); return null; });
    setMeasuring(false);
    setForm({ campaignType: 'OUTCOME_LEADS', format: '', assetName: '' });
    setError(null);
  };

  const confirmUpload = async () => {
    if (!pendingFile || !form.assetName.trim() || !accountId) return;
    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', pendingFile);
    formData.append('client_account_id', accountId);
    formData.append('asset_name', form.assetName.trim());
    formData.append('campaign_type', form.campaignType);
    formData.append('format', form.format);

    try {
      const name = form.assetName.trim();
      await apiPostForm('/upload-creative', formData);
      cancelUpload();
      loadAssets();
      setToast({ text: `${name} added. It is pending approval.` });
    } catch (err) {
      // The server's own message, not a generic substitute. When Meta rejected
      // a file for an unsupported type, "Upload failed" sent us to the logs to
      // find out something the server already knew.
      setError(err.message || 'Upload failed. The asset was not saved.');
    }
    setUploading(false);
  };

  /* ── Approve ────────────────────────────────────────────────────────── */

  const approve = async (asset) => {
    setError(null);
    setApprovingId(asset.asset_id);
    try {
      await apiPatch(`/creative-library/${asset.asset_id}/approve`);
      setConfirming(null);
      setPreview(null);
      loadAssets();
      setToast({ text: `${asset.asset_name} approved and published to Meta.` });
    } catch (err) {
      setConfirming(null);
      setError(err.message || 'Could not approve this asset.');
    }
    setApprovingId(null);
  };

  /* ── Filtering ──────────────────────────────────────────────────────── */

  const counts = useMemo(() => assets.reduce((acc, a) => {
    const status = statusOf(a);
    acc.ALL += 1;
    if (status === 'live') acc.LIVE += 1;
    else if (status === 'needs') acc.NEEDS += 1;
    else acc.PENDING += 1;
    return acc;
  }, { ALL: 0, PENDING: 0, NEEDS: 0, LIVE: 0 }), [assets]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return assets.filter((a) => {
      const status = statusOf(a);
      if (filter === 'PENDING' && status !== 'pending') return false;
      if (filter === 'NEEDS' && status !== 'needs') return false;
      if (filter === 'LIVE' && status !== 'live') return false;
      if (needle && !(a.asset_name || '').toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [assets, filter, search]);

  const duplicateWarning = isDuplicate(form.assetName);
  const canUpload = pendingFile && form.assetName.trim() && !duplicateWarning && !uploading && !measuring;

  /* ── Render ─────────────────────────────────────────────────────────── */

  // Creative belongs to one client, and uploading needs a destination account.
  if (isAll) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Creative Library</h1>
          <p className="page-subtitle">Creative assets are held per client</p>
        </div>
        <div className="card">
          <div className="empty-state">
            <h3>Select a client</h3>
            <p>Choose a client from the switcher above to view or upload creative.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header split">
        <div>
          <h1 className="page-title">Creative Library</h1>
          <p className="page-subtitle">
            {selected ? `${selected.name} · ` : ''}
            assets are published to Meta on approval. Agents can only use what is approved here.
          </p>
        </div>
        <div className="hdr-actions">
          <div className="seg" role="group" aria-label="View">
            <button aria-pressed={view === 'grid'} onClick={() => setView('grid')}>
              <Icon name="cards" size={13} strokeWidth={2.2} /> Grid
            </button>
            <button aria-pressed={view === 'list'} onClick={() => setView('list')}>
              <Icon name="layers" size={13} strokeWidth={2.2} /> List
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="banner bad">
          <Icon name="alert" size={16} strokeWidth={2.2} />
          <span>{error}</span>
        </div>
      )}

      {/* Outside the drop zone on purpose. When the input lived inside it, the
          click this handler fires on the input bubbled straight back to the
          drop zone and fired the handler again. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif"
        style={{ display: 'none' }}
        onChange={(e) => e.target.files[0] && selectFile(e.target.files[0])}
      />

      {!pendingFile ? (
        <button
          type="button"
          className={`cl-drop${dragOver ? ' over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) selectFile(file);
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <span className="cl-drop-icon"><Icon name="image" size={20} strokeWidth={1.9} /></span>
          <span>
            <span className="cl-drop-h">Drag a creative here, or click to browse</span>
            <span className="cl-drop-sub">JPG, PNG or GIF, up to 8MB. Video is not yet supported.</span>
          </span>
        </button>
      ) : (
        <div className="card cl-name">
          <div className="cl-name-prev">
            <div className="cl-name-shot cl-checker">
              {localPreview
                ? <img src={localPreview} alt="The file you are about to upload" />
                : <Icon name="image" size={26} strokeWidth={1.7} />}
            </div>
            <div className="cl-name-facts">
              <b>{pendingFile.name}</b>
              {[form.format, fileSize(pendingFile.size)].filter(Boolean).join(' · ')}
            </div>
          </div>

          <div>
            <div className="cl-name-h">Name this asset</div>
            <p className="cl-name-lede">
              This is the name an agent will use to reference the creative, so use whatever
              convention your team already works with.
            </p>

            <div className="form-group">
              <label className="form-label" htmlFor="cl-asset-name">Asset name</label>
              <input
                id="cl-asset-name"
                className="form-input"
                value={form.assetName}
                onChange={(e) => setForm((f) => ({ ...f, assetName: e.target.value }))}
                placeholder="e.g. Summer 26 Hero 16:9"
                maxLength={200}
                autoFocus
                style={duplicateWarning ? { borderColor: 'var(--bad)' } : undefined}
              />
              {duplicateWarning ? (
                <div className="cl-hint bad">
                  An asset with this name already exists. Names are unique per client, ignoring
                  case, so an agent always resolves to exactly one file.
                </div>
              ) : (
                form.format && (
                  <div className="cl-hint">
                    <button
                      type="button"
                      className="cl-linkbtn"
                      onClick={() => setForm((f) => ({ ...f, assetName: suggestName(f.format, f.campaignType) }))}
                    >
                      Use suggested name
                    </button>
                  </div>
                )
              )}
            </div>

            <div className="cl-name-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="cl-campaign-type">Campaign type</label>
                <select
                  id="cl-campaign-type"
                  className="form-select"
                  value={form.campaignType}
                  onChange={(e) => setForm((f) => ({ ...f, campaignType: e.target.value }))}
                >
                  {CAMPAIGN_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <div className="cl-hint">Campaigns with a different objective will be flagged for review.</div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="cl-format">Format</label>
                <input
                  id="cl-format"
                  className="form-input"
                  value={form.format}
                  onChange={(e) => setForm((f) => ({ ...f, format: e.target.value }))}
                  placeholder="e.g. 1080x1080"
                />
                <div className="cl-hint">Read from the file. Change it if your team labels sizes differently.</div>
              </div>
            </div>

            <div className="cl-actions">
              <button className="btn btn-approve" onClick={confirmUpload} disabled={!canUpload}>
                {uploading ? 'Uploading…' : measuring ? 'Reading image…' : 'Upload'}
              </button>
              <button className="btn btn-secondary" onClick={cancelUpload} disabled={uploading}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="cl-toolbar">
        <div className="seg" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button key={f.value} aria-pressed={filter === f.value} onClick={() => setFilter(f.value)}>
              {f.label}
              {counts[f.value] > 0 && f.value !== 'ALL' ? ` (${counts[f.value]})` : ''}
            </button>
          ))}
        </div>

        <div className="cl-search">
          <Icon name="search" size={15} strokeWidth={2.2} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name"
            aria-label="Search assets by name"
          />
        </div>

        <span className="cl-count">
          {filtered.length} of {assets.length} {assets.length === 1 ? 'asset' : 'assets'}
        </span>
      </div>

      {loading ? (
        <div className="cl-grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div className="cl-card" key={i}>
              <div className="skeleton" style={{ aspectRatio: '4 / 3', borderRadius: 0 }} />
              <div className="cl-body">
                <div className="skeleton skeleton-line w80" />
                <div className="skeleton skeleton-line w40" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h3>{assets.length === 0 ? 'No assets yet' : 'Nothing matches'}</h3>
            <p>
              {assets.length === 0
                ? 'Upload your first creative above. Nothing is published to Meta until you approve it.'
                : 'Try a different status or clear the search.'}
            </p>
          </div>
        </div>
      ) : view === 'grid' ? (
        <div className="cl-grid">
          {filtered.map((asset) => (
            <AssetCard
              key={asset.asset_id}
              accountId={accountId}
              asset={asset}
              onOpen={setPreview}
              onApprove={setConfirming}
            />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="tbl-wrap">
            <table className="data-table dense">
              <thead>
                <tr>
                  <th style={{ width: 72 }}>Preview</th>
                  <th>Asset name</th>
                  <th>Format</th>
                  <th>Campaign type</th>
                  <th>Added</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.map((asset) => (
                  <tr key={asset.asset_id}>
                    <td>
                      <Thumbnail
                        accountId={accountId}
                        asset={asset}
                        onOpen={setPreview}
                        className="cl-tt"
                        compact
                      />
                    </td>
                    <td>
                      <button className="cl-namebtn" onClick={() => setPreview(asset)}>
                        {asset.asset_name}
                      </button>
                    </td>
                    <td>{asset.format || '—'}</td>
                    <td>{asset.campaign_type ? (TYPE_LABEL[asset.campaign_type] || asset.campaign_type) : '—'}</td>
                    <td>{ago(asset.created_at) || '—'}</td>
                    <td><StatusPill asset={asset} /></td>
                    <td>
                      {!asset.is_approved && (
                        <button
                          className="btn btn-approve"
                          style={{ padding: '4px 12px', fontSize: 12 }}
                          onClick={() => setConfirming(asset)}
                        >
                          Approve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {preview && (
        <PreviewDialog
          accountId={accountId}
          asset={preview}
          onClose={() => setPreview(null)}
          onApprove={(asset) => setConfirming(asset)}
          onEdit={(asset) => navigate(`/creative/studio?asset=${asset.asset_id}`)}
        />
      )}

      {confirming && (
        <ApproveDialog
          accountId={accountId}
          asset={confirming}
          busy={approvingId === confirming.asset_id}
          onCancel={() => setConfirming(null)}
          onConfirm={approve}
        />
      )}

      {toast && (
        <div className="toast-host">
          <div className="toast">
            <Icon name="check" size={16} strokeWidth={2.6} />
            {toast.text}
          </div>
        </div>
      )}
    </>
  );
}
